"use client";

import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useRewardsToday, useRewardsSummary, type RewardsSummary } from "@/hooks/usePolymarketRewards";
import { formatCurrency } from "@/lib/format";
import { useT } from "@/hooks/useT";

type Range = RewardsSummary["range"];

const RANGE_KEYS: Array<{ key: Range; tKey: string }> = [
  { key: "today", tKey: "portfolio.rewards.range.today" },
  { key: "7d", tKey: "portfolio.rewards.range.7d" },
  { key: "30d", tKey: "portfolio.rewards.range.30d" },
  { key: "lifetime", tKey: "portfolio.rewards.range.lifetime" },
];

export default function PolymarketRewardsCard() {
  const t = useT();
  const { isLoggedIn } = useAuth();
  const [range, setRange] = useState<Range>("today");

  const todayQ = useRewardsToday(isLoggedIn);
  const summaryQ = useRewardsSummary(range, isLoggedIn && range !== "today");

  if (!isLoggedIn) return null;

  const today = todayQ.data;
  const summary = summaryQ.data;
  const activeSnap = range === "today"
    ? {
        totalLiquidityRewardUsd: today?.totalLiquidityRewardUsd ?? 0,
        totalMakerRebateUsd: today?.totalMakerRebateUsd ?? 0,
      }
    : {
        totalLiquidityRewardUsd: summary?.totalLiquidityRewardUsd ?? 0,
        totalMakerRebateUsd: summary?.totalMakerRebateUsd ?? 0,
      };
  const total = activeSnap.totalLiquidityRewardUsd + activeSnap.totalMakerRebateUsd;
  const loading = (range === "today" ? todayQ.isLoading : summaryQ.isLoading);
  const hasAny = total > 0 || (today?.earningMarketsCount ?? 0) > 0;

  return (
    <div className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#0a0a0b] p-4">
      <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t("portfolio.rewards.title")}</h3>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {t("portfolio.rewards.subtitle")}
          </p>
        </div>
        <div className="flex rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden">
          {RANGE_KEYS.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-2 py-1 text-[11px] font-medium transition-colors ${
                range === r.key
                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#0a0a0b]/50"
              }`}
            >
              {t(r.tKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">{t("portfolio.rewards.total")}</div>
          <div className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white tabular-nums">
            {loading ? "…" : formatCurrency(total)}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">{t("portfolio.rewards.liquidity")}</div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {loading ? "…" : formatCurrency(activeSnap.totalLiquidityRewardUsd)}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">{t("portfolio.rewards.rebates")}</div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {loading ? "…" : formatCurrency(activeSnap.totalMakerRebateUsd)}
          </div>
        </div>
      </div>

      {range === "today" && (today?.earningMarketsCount ?? 0) > 0 && (
        <div className="mt-4 border-t border-gray-200 dark:border-[#1A1A1A] pt-3">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-2">
            {today!.earningMarketsCount === 1
              ? t("portfolio.rewards.earningOnOne", { n: today!.earningMarketsCount })
              : t("portfolio.rewards.earningOnMany", { n: today!.earningMarketsCount })}
          </div>
          <ul className="space-y-1.5 max-h-40 overflow-y-auto">
            {today!.byMarket
              .filter((m) => m.liquidityRewardUsd + m.makerRebateUsd > 0)
              .slice(0, 10)
              .map((m) => {
                const total = m.liquidityRewardUsd + m.makerRebateUsd;
                // Sub-cent rows display "<$0.01" — rendering "$0.00" next to
                // a non-zero header total is confusing, and a user can't
                // reconcile the list sum. This makes them visibly present.
                const display = total < 0.01 ? "<$0.01" : formatCurrency(total);
                return (
                  <li
                    key={m.conditionId}
                    className="flex items-center justify-between text-[12px]"
                  >
                    <span className="truncate text-gray-700 dark:text-gray-300 max-w-[220px]">
                      {m.question || m.marketSlug || m.conditionId.slice(0, 10)}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {display}
                    </span>
                  </li>
                );
              })}
          </ul>
        </div>
      )}

      {!loading && !hasAny && (
        <div className="mt-3 text-[11px] text-gray-500 dark:text-gray-400">
          {t("portfolio.rewards.empty")}
        </div>
      )}
    </div>
  );
}
